import { Box, Container, IconButton, Typography } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { checkBetterHelpUser, nextSessions } from '@uplift/api';
import { useFeatureFlag, useIsMobile, useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole } from '@uplift/types';
import { ClosableMenu, ClosableMenuItem, Loader, PublicICSSubscriptionModal, StandardTimezoneSelect, UpliftThemeProvider } from '@uplift/ui-v2';
import React from 'react';
import { MainLayout } from '../../components';
import { ActionItems } from './components/action-items/action-items';
import { DashboardUpcoming } from './components/dashboard-upcoming';
import { ProvidersSideBar } from './components/providers-sidebar/providers-sidebar.component';
import { useCurrentuser } from '@uplift/contexts';
import { usePatientDetailsQuery, useUserMatchesQuery } from '@uplift/queries';
import { MoreHoriz } from '@mui/icons-material';

export const useSessions = () => {
  const queryClient = useQueryClient();
  const { data: nextSessionsData = [], isLoading: isNextSessionsLoading } = useQuery({
    queryKey: [ 'next-sessions' ],
    queryFn: () => nextSessions({ limit: 1 }).then(res => res.data)
  });
  const allFromDatetime = nextSessionsData?.[0]?.fromDatetime;
  const { data: allSessionsData = [] } = useQuery({
    queryKey: [ 'all-sessions' ],
    queryFn: async () => {
      const dtFrom = CalendarDateService.date(allFromDatetime).add(1, 'second');
      const sessionsResponse = await nextSessions({
        limit: 5,
        dt_from: dtFrom.format(),
        appointments_per_series: 1,
        include_psychiatry_session: true
      });

      return sessionsResponse.data;
    },
    enabled: !!allFromDatetime
  });


  React.useEffect(() => {
    if (allFromDatetime) {
      queryClient.invalidateQueries({ queryKey: [ 'all-sessions' ] });
    }
  }, [ allFromDatetime ]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    nextSessionsData,
    allSessionsData,
    isNextSessionsLoading,
  };
};

export const DashboardContainer = withStyles(Container, theme => ({
  maxWidthLg: {
    [theme.breakpoints.up('lg')]: {
      maxWidth: '1400px',
    },
  },
}));

export const DashboardPage: React.FC = () => {
  const l10n = useL10n();
  const me = useCurrentuser();
  const queryClient = useQueryClient();
  const USER_PUBLIC_ICS_SUBSCRIPTION = useFeatureFlag('USER_PUBLIC_ICS_SUBSCRIPTION');
  const [ subscribeToPublicICSModalOpen, setSubscribeToPublicICSMModalOpen ] = React.useState(false);
  const [ anchorEl, setAnchorEl ] = React.useState<HTMLElement>();
  const { nextSessionsData, allSessionsData, isNextSessionsLoading } = useSessions();
  const { data: matches, isLoading: isMatchesLoading } = useUserMatchesQuery(me.id);
  const { data: patientDetails } = usePatientDetailsQuery(me.id);
  const activeMatches = matches?.activeMatches || [];
  const hasTherapist = !!activeMatches.find(m => m.provider.medicalRole === ProviderMedicalRole.therapist);
  const { classes } = useStyles();
  const isMobile = useIsMobile('sm');
  const needToMatchTherapist = !hasTherapist && !checkBetterHelpUser(me, patientDetails);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    const timeToNextMinute = CalendarDateService.getSecondsToNextMinute();

    const refetch = () => {
      queryClient.invalidateQueries({ queryKey: [ 'next-sessions' ] });
      const timeToNextMinute = CalendarDateService.getSecondsToNextMinute();

      timeout = setTimeout(refetch, timeToNextMinute * 1000);
    };

    timeout = setTimeout(refetch, timeToNextMinute * 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <MainLayout loading={isNextSessionsLoading || isMatchesLoading} noContainerPadding={isMobile} className={classes.greyBackground}>
      <UpliftThemeProvider>
        <DashboardContainer disableGutters>
          <Box className={classes.grid}>
            <Box className={classes.title}>
              <Box mb={3} ml={{ xs: 2, sm: 0 }}>
                <Typography variant="h3">
                  <Box display="flex" justifyContent="space-between" alignItems="flex-end">
                    {l10n.sessions.nextSessions}
                    {USER_PUBLIC_ICS_SUBSCRIPTION ? (
                      <Box flex={1} px={1}>
                        <IconButton title="Settings" size="small" component="span" onClick={e => setAnchorEl(e.target as HTMLElement)}>
                          <MoreHoriz color="inherit" />
                        </IconButton>
                        <ClosableMenu open={!!anchorEl} anchorEl={anchorEl} onClose={() => setAnchorEl(undefined)}>
                          <ClosableMenuItem onClick={async () => setSubscribeToPublicICSMModalOpen(true)}>
                            Subscribe to calendar
                          </ClosableMenuItem>
                        </ClosableMenu>
                      </Box>
                    ) : <Box flex={1} />}
                    <StandardTimezoneSelect classes={{ buttonIcon: classes.timezoneIcon }} />
                  </Box>
                </Typography>
              </Box>
              <DashboardUpcoming
                refetchAppointments={() => {
                  queryClient.invalidateQueries({ queryKey: [ 'next-sessions' ] });
                  queryClient.invalidateQueries({ queryKey: [ 'all-sessions' ] });
                  queryClient.invalidateQueries({ queryKey: [ 'recurringList' ] });
                  queryClient.invalidateQueries({ queryKey: [ 'next-sessions-for-provider' ] });
                }}
                nextSessions={nextSessionsData}
                allSessions={allSessionsData}
                needToMatchTherapist={needToMatchTherapist}
              />
            </Box>
            <Box className={classes.actionItems}>
              <ActionItems />
            </Box>
            {(isMatchesLoading || activeMatches?.length > 0) && (
              <React.Suspense fallback={<Loader height="300px" />}>
                <Box className={classes.careTeam}>
                  <Box mb={3} ml={{ xs: 2, sm: 0 }}>
                    <Typography variant="h3">
                      {l10n.pages.yourCareTeam}
                    </Typography>
                  </Box>
                  <Box>
                    <ProvidersSideBar
                      matches={activeMatches}
                      isLoading={isMatchesLoading}
                    />
                  </Box>
                </Box>
              </React.Suspense>
            )}
          </Box>
        </DashboardContainer>
        <PublicICSSubscriptionModal open={subscribeToPublicICSModalOpen} onClose={() => setSubscribeToPublicICSMModalOpen(false)} />
      </UpliftThemeProvider>
    </MainLayout>
  );
};

const useStyles = makeStyles()(theme => ({
  greyBackground: {
    background: theme.palette.grey[50],
  },
  grid: {
    display: 'grid',
    gridRowGap: theme.spacing(5),
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(3, auto)',
    [theme.breakpoints.up('md')]: {
      gridColumnGap: theme.spacing(3),
      gridTemplateColumns: 'repeat(3, 1fr)',
      gridTemplateRows: 'repeat(3, 1fr)',
    }
  },
  title: {
    gridArea: '1 / 1 / 2 / 2',
    [theme.breakpoints.up('md')]: {
      gridArea: '1 / 1 / 2 / 3'
    }
  },
  careTeam: {
    gridArea: '3 / 1 / 4 / 2',
    [theme.breakpoints.up('md')]: {
      gridArea: '1 / 3 / 4 / 4'
    }
  },
  actionItems: {
    gridArea: '2 / 1 / 3 / 2',
    [theme.breakpoints.up('md')]: {
      gridArea: '2 / 1 / 4 / 3'
    }
  },
  fullWidth: {
    padding: '1rem !important',
    [theme.breakpoints.up('md')]: {
      padding: 'unset'
    }
  },
  timezoneIcon: {
    fontSize: '24px !important',
  }
}));
